import { useEffect, useRef, useState } from "react";
import api from "./api";

/**
 * Polls the lightweight /products/stock/live endpoint and returns a stock map
 * keyed by SKU — { [sku]: { stock_quantity, stock_status, ts } }.
 *
 * Usage:
 *   const stockMap = useLiveStock();
 *   const live = stockMap[product.sku];
 *   if (live?.stock_quantity <= 0) ...
 *
 * - Poll interval: 15s (balances freshness vs server load).
 * - Pauses while the browser tab is hidden to save bandwidth.
 * - Exposes `ts` so consumers can animate on each update.
 */
export default function useLiveStock(pollMs = 15000) {
  const [map, setMap] = useState({});
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const r = await api.get("/products/stock/live");
        if (cancelled) return;
        const ts = r.data?.ts || new Date().toISOString();
        const next = {};
        (r.data?.items || []).forEach((it) => {
          next[it.sku] = {
            stock_quantity: it.stock_quantity,
            stock_status: it.stock_status,
            ts,
          };
        });
        setMap(next);
      } catch {
        /* silent — keep last good state */
      }
    };

    fetchOnce();
    const start = () => {
      stop();
      timerRef.current = setInterval(fetchOnce, pollMs);
    };
    const stop = () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else {
        fetchOnce();
        start();
      }
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [pollMs]);

  return map;
}
